'use client'
import { faGripVertical } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export const useRowDrag = ({
  draggable = false,
  isInteractionLocked = false,
  rows = [],
  onReorder,
}) => {
  const theme = useTheme()
  const [draggingIndex, setDraggingIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const rowElementRefs = useRef({})
  const dragPreviewRef = useRef(null)

  const removeDragPreview = useCallback(() => {
    if (dragPreviewRef.current?.parentNode) {
      dragPreviewRef.current.parentNode.removeChild(dragPreviewRef.current)
    }
    dragPreviewRef.current = null
  }, [])

  useEffect(() => () => {
    removeDragPreview()
  }, [removeDragPreview])

  const clearDragState = useCallback(() => {
    setDraggingIndex(null)
    setDragOverIndex(null)
    removeDragPreview()
  }, [removeDragPreview])

  const setRowElementRef = useCallback((index, element) => {
    if (element) {
      rowElementRefs.current[index] = element
      return
    }
    delete rowElementRefs.current[index]
  }, [])

  const setDragImageFromRow = useCallback((index, event) => {
    const rowElement = rowElementRefs.current[index]
    if (!rowElement) return

    removeDragPreview()

    const rect = rowElement.getBoundingClientRect()
    const dragPreview = rowElement.cloneNode(true)
    dragPreview.style.position = 'fixed'
    dragPreview.style.top = '-1000px'
    dragPreview.style.left = '-1000px'
    dragPreview.style.pointerEvents = 'none'
    dragPreview.style.opacity = '0.78'
    dragPreview.style.width = `${rect.width}px`
    dragPreview.style.zIndex = '2000'

    document.body.appendChild(dragPreview)
    dragPreviewRef.current = dragPreview

    const offsetX = Math.max(12, Math.min(rect.width - 12, event.clientX - rect.left))
    event.dataTransfer.setDragImage(dragPreview, offsetX, 16)
  }, [removeDragPreview])

  const handleRowDragOver = useCallback((event) => {
    if (!draggable || isInteractionLocked || draggingIndex === null) return

    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [draggable, draggingIndex, isInteractionLocked])

  const handleRowDrop = useCallback((event, targetIndex) => {
    if (!draggable || isInteractionLocked) return

    event.preventDefault()
    event.stopPropagation()

    const parsedSource = Number(event.dataTransfer.getData('text/plain'))
    const sourceIndex = draggingIndex !== null ? draggingIndex : parsedSource

    if (Number.isNaN(sourceIndex) || sourceIndex === targetIndex) {
      clearDragState()
      return
    }

    const sourceRows = Array.isArray(rows) ? [...rows] : []
    if (
      sourceIndex < 0
      || sourceIndex >= sourceRows.length
      || targetIndex < 0
      || targetIndex >= sourceRows.length
    ) {
      clearDragState()
      return
    }

    const [movedRow] = sourceRows.splice(sourceIndex, 1)
    sourceRows.splice(targetIndex, 0, movedRow)

    if (typeof onReorder === 'function') {
      onReorder(sourceRows)
    }

    clearDragState()
  }, [clearDragState, draggable, draggingIndex, isInteractionLocked, onReorder, rows])

  const dragColumn = useMemo(() => {
    if (!draggable) return []

    return [{
      id: '__drag__',
      header: '',
      enableSorting: false,
      enableFiltering: false,
      cell: ({ row }) => (
        <Box
          component="span"
          draggable={!isInteractionLocked}
          onClick={(event) => event.stopPropagation()}
          onDragStart={(event) => {
            if (isInteractionLocked) return

            event.stopPropagation()
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', String(row.index))
            setDraggingIndex(row.index)
            setDragOverIndex(row.index)
            setDragImageFromRow(row.index, event)
          }}
          onDragEnd={clearDragState}
          title="Drag to reorder"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.text.secondary,
            cursor: isInteractionLocked ? 'not-allowed' : 'grab',
          }}
        >
          <Box component={FontAwesomeIcon} icon={faGripVertical} width={12} color={isInteractionLocked ? 'action.disabled' : 'primary.main'} />
        </Box>
      ),
    }]
  }, [clearDragState, draggable, isInteractionLocked, setDragImageFromRow, theme.palette.text.secondary])

  return {
    dragColumn,
    draggingIndex,
    dragOverIndex,
    setRowElementRef,
    handleRowDragOver,
    setDragOverIndex,
    handleRowDrop,
  }
}